import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { ContactSearchInput } from "lib/generated/app-service-gql/graphql";
import { getUserFacingMessage } from "lib/graphqlErrors";
import { ContactSearchResult } from "lib/hooks/api/contacts/useContactSearch";
import { useExportCognismContacts } from "lib/hooks/api/contacts/useExportCognismContacts";
import { useFileExportStatus } from "lib/hooks/api/exports/useFileExportStatus";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { Format } from "lib/types/graphQLEnums";
import { downloadFileViaFetch, pluralise } from "lib/utils";
import ContactCreditsPopover from "./ContactCreditsPopover";
import { contactRevealRef, contactRevealState } from "./contactSource";
import { revealEventData } from "./contactTracking";

// Client-side UX cap on rows per export (distinct from the server's EXPORT_TECHNICAL_LIMIT reliability
// ceiling). Keeps CSVs small and the projected credit cost meaningful.
const EXPORT_LIMIT = 500;

const BAD_REQUEST = 400;

// Only rows the company has not yet revealed cost a credit, whichever source they come from.
function chargeableCount(contacts: ContactSearchResult[]): number {
  return contacts.filter((c) => contactRevealState(c) === "revealable").length;
}

/**
 * Report the two shortfalls separately, matching the bulk-reveal bar's wording. They are
 * different outcomes with different user actions — privacy suppression is permanent, a provider
 * failure is worth retrying — and collapsing both into one "N unavailable" hid a matching bug
 * in the redeem reconciliation for as long as it existed.
 */
function exportResultMessage(
  revealedCount: number,
  suppressedCount: number,
  failedCount: number,
): string {
  const notes: string[] = [];
  if (suppressedCount > 0) notes.push(`${suppressedCount} unavailable (privacy)`);
  if (failedCount > 0) notes.push(`${failedCount} couldn't be retrieved — try again`);
  const suffix = notes.length > 0 ? ` ${notes.join(". ")}.` : "";
  return `Exporting ${pluralise(revealedCount, "contact")}.${suffix}`;
}

type Props = {
  /** The current page's selected rows. When non-empty, export targets exactly these. */
  selectedContacts: ContactSearchResult[];
  /** Total matching the current search — the count shown (and exported) when nothing is selected. */
  totalResults: number | null;
  /** The active search; used to export everything matching when nothing is selected. */
  filter: ContactSearchInput;
  /**
   * Rendered inside the bulk-action bar, which sits on sysPrimaryDefault: drop the default
   * secondary's white fill so the CTA matches the bar's outlined buttons (the outline itself
   * comes from the bar's wrapper). Default is the page-header treatment.
   */
  inActionBar?: boolean;
};

/**
 * Top-right "Export" for Cognism contacts. Exports the selection when rows are picked, otherwise
 * everything matching the current search. Unredeemed rows are redeemed on the backend before the
 * file is written, charging one CONTACTS credit each; a pre-export confirm shows the projected cost.
 */
export default function ExportCognismContactsButton({
  selectedContacts,
  totalResults,
  filter,
  inActionBar = false,
}: Props) {
  const { logEvent } = useTracking();

  const { mutate, data, isLoading, reset } = useExportCognismContacts({
    onSuccess: (res) => {
      const { revealedCount, suppressedCount, failedCount } = res.exportCognismContacts;
      const message = exportResultMessage(revealedCount, suppressedCount, failedCount);
      // A provider failure is worth retrying, so it reads as an error even though the file still
      // downloads; privacy suppression is permanent, so it's information, not a failure.
      if (failedCount > 0) {
        toast.error(message);
        return;
      }
      if (suppressedCount > 0) {
        toast.info(message);
        return;
      }
      toast.success(message);
    },
    // A refused export (not enough credits, too many rows) says why; anything else is retryable.
    onError: (error) =>
      toast.error(
        getUserFacingMessage(error, [BAD_REQUEST]) ?? "Couldn't export contacts. Please try again.",
      ),
  });

  const { data: exportStatus } = useFileExportStatus({
    exportId: data?.id,
    onSuccess: async (downloadUrl) => {
      await downloadFileViaFetch(
        downloadUrl,
        `stotles-contacts-${new Date().toJSON().slice(0, 10)}.csv`,
      );
      logEvent(EventNames.exportedData, {
        Count: data?.revealedCount ?? 0,
        "Data type": EventDataTypes.contact,
        Format: Format.Csv,
      });
      reset();
    },
    onError: () => {
      toast.error("Export failed. Please try again later.");
      reset();
    },
  });

  const isExporting =
    isLoading ||
    !!(data?.id && !exportStatus?.fileExport.isComplete && !exportStatus?.fileExport.isError);

  const hasSelection = selectedContacts.length > 0;
  const rawTotal = totalResults ?? 0;
  const count = hasSelection ? selectedContacts.length : rawTotal;

  // Block over-cap here (too-large selection or too-broad export-all) rather than letting the request
  // fail. EXPORT_LIMIT is the client UX cap; the server also refuses > EXPORT_TECHNICAL_LIMIT.
  const overLimit = count > EXPORT_LIMIT;
  const overLimitTooltip = hasSelection
    ? `Deselect down to ${EXPORT_LIMIT} or fewer contacts to export.`
    : `Narrow results to ${EXPORT_LIMIT} or fewer, or select contacts, to export.`;

  // Credits this export will consume. For a selection it's the unrevealed rows (precise). For
  // export-all the cost is unknowable client-side (mostly free/owned rows), so pass null and don't
  // credit-gate — the server charges only new reveals and refuses an over-budget export authoritatively.
  const marginalCredits = hasSelection ? chargeableCount(selectedContacts) : null;

  const onConfirm = () => {
    // Source-tagged refs. Cognism redeems by redeemId (`id`) and also carries the stable
    // cognismContactId (the node id) as the durable charge/dedup key; Oscar by its node id.
    const input = hasSelection
      ? {
          contacts: selectedContacts.flatMap((c) => {
            const ref = contactRevealRef(c);
            return ref ? [ref] : [];
          }),
        }
      : { filter };

    const scope = {
      "Contact count": count,
      "Projected credits": marginalCredits,
      "Selection type": hasSelection ? "Selection" : "All results",
    };
    // Attempt and outcome are separate events so the drop-off between them is visible.
    logEvent(EventNames.contactsExportStarted, {
      "Data type": EventDataTypes.contact,
      ...scope,
    });

    mutate(
      { input, exportFormat: { fileFormat: Format.Csv } },
      {
        onSuccess: (res) => {
          // Credits are spent here, not at download: `chargedCount` is what was actually
          // billed, while `revealedCount` counts every row in the file (held rows are free).
          const { revealedCount, suppressedCount, failedCount, chargedCount } =
            res.exportCognismContacts;
          logEvent(EventNames.contactsRevealed, {
            ...revealEventData("Export", {
              revealedCount: chargedCount,
              suppressedCount,
              failedCount,
            }),
            "Exported count": revealedCount,
            ...scope,
          });
        },
      },
    );
  };

  const exportButton = (
    <Button
      buttonType={inActionBar ? "primary" : "secondary"}
      icon="download01"
      loading={isExporting}
      disabled={overLimit || count === 0}
    >
      Export ({count})
    </Button>
  );

  // Over the server cap: block on the trigger with an explanatory tooltip (no popover).
  if (overLimit) {
    return (
      <Tooltip content={overLimitTooltip}>
        <span>{exportButton}</span>
      </Tooltip>
    );
  }

  return (
    <ContactCreditsPopover
      trigger={exportButton}
      title={hasSelection ? "Export all selected contacts" : "Export all contacts in this search"}
      marginalCredits={marginalCredits}
      onConfirm={onConfirm}
      disabled={count === 0}
    />
  );
}
